import { Button, Form, Input, message, Modal, Select, Typography } from 'antd';
import { useState } from 'react';
import axios from '../../config/axios';
import { handleAPIError } from '../../utils/helpers';
import { useQueryClient } from '@tanstack/react-query';
import CopyableText from '../../components/copyable-text';
import { BsPhone } from 'react-icons/bs';
import AppLayout from '../../layouts/app.layout';

const { Title, Text } = Typography;

interface AdminFormValues {
	phone_number: string;
	first_name: string;
	last_name: string;
	email: string;
	password: string;
	staff_type: string;
}

const CreateAdmin = () => {
	const [form] = Form.useForm<AdminFormValues>();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isComplete, setIsComplete] = useState<boolean>(false);
	const [registeredUser, setRegisteredUser] = useState<{ phone_number: string; password: string } | null>(null);
	const [countryCode, setCountryCode] = useState<string>('256');
	const [showModal, setShowModal] = useState<boolean>(false);

	const queryClient = useQueryClient();

	const handleAdminRegistration = async (values: AdminFormValues) => {
		setIsLoading(true);
		try {
			// Add country code to phone number
			const phoneWithCode = `${countryCode}${values.phone_number}`;

			await axios.post(
				'/users/register/admin',
				{ ...values, phone_number: phoneWithCode, role: 'admin' },
				{
					withCredentials: true,
				},
			);

			// Store the user credentials for the success screen
			setRegisteredUser({
				phone_number: phoneWithCode,
				password: values.password,
			});

			message.success('Admin account created successfully!');
			setIsComplete(true);
			setShowModal(true);

			// Refetch the users query
			queryClient.invalidateQueries({ queryKey: ['users'] });

			// Reset form
			form.resetFields();
		} catch (error) {
			handleAPIError(error as Error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleModalClose = () => {
		setShowModal(false);
		setIsComplete(false);
		setRegisteredUser(null);
	};

	const handleStartOver = () => {
		setIsComplete(false);
		setRegisteredUser(null);
		form.resetFields();
	};

	const selectBefore = (
		<Select
			defaultValue={'+256'}
			prefix={<BsPhone />}
			onChange={(value) => setCountryCode(value)}
		>
			<Select.Option value="256">+256</Select.Option>
			<Select.Option value="88">+88</Select.Option>
		</Select>
	);

	const modalContent = isComplete ? (
		<div className="py-8 text-center">
			<div className="mb-6 text-center">
				<div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
					<span className="text-2xl text-green-500">✓</span>
				</div>
				<Title level={3}>Registration Complete!</Title>
				<Text className="text-gray-500">The admin has been successfully registered</Text>

				{registeredUser && (
					<div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4 text-left">
						<Title
							level={5}
							className="mb-3"
						>
							Admin Login Credentials
						</Title>
						<div className="space-y-3">
							<div>
								<Text strong>Phone Number:</Text>
								<div className="mt-1">
									<CopyableText text={registeredUser.phone_number} />
								</div>
							</div>
							<div>
								<Text strong>Password:</Text>
								<div className="mt-1">
									<CopyableText text={registeredUser.password} />
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
			<Button
				type="primary"
				onClick={handleStartOver}
			>
				Register Another Admin
			</Button>
		</div>
	) : (
		<Form
			form={form}
			layout="vertical"
			onFinish={handleAdminRegistration}
			requiredMark={false}
			disabled={isLoading}
			initialValues={{ staff_type: 'support' }}
		>
			<Title level={4}>Register Admin Account</Title>
			<Text
				type="secondary"
				className="mb-6 block"
			>
				Create a new admin account
			</Text>

			<Form.Item
				name="phone_number"
				label="Phone Number"
				rules={[
					{ required: true, message: 'Please enter the phone number' },
					{ pattern: /^\d+$/, message: 'Please enter a valid phone number' },
				]}
			>
				<Input
					addonBefore={selectBefore}
					placeholder="Phone number"
					onKeyDown={(e) => {
						// If the key is not a number (0-9), prevent input
						if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
							e.preventDefault();
						}
					}}
					onChange={(e) => {
						// Remove any non-numeric characters
						e.target.value = e.target.value.replace(/\D/g, '');
					}}
				/>
			</Form.Item>

			<Form.Item
				name="first_name"
				label="First Name"
				rules={[{ required: true, message: 'Please enter the first name' }]}
			>
				<Input placeholder="First name" />
			</Form.Item>

			<Form.Item
				name="last_name"
				label="Last Name"
				rules={[{ required: true, message: 'Please enter the last name' }]}
			>
				<Input placeholder="Last name" />
			</Form.Item>

			<Form.Item
				name="email"
				label="Email"
				rules={[
					{ required: true, message: 'Please enter the email' },
					{ type: 'email', message: 'Please enter a valid email' },
				]}
			>
				<Input placeholder="Email address" />
			</Form.Item>

			<Form.Item
				name="password"
				label="Password"
				rules={[{ required: true, message: 'Please enter a password' }]}
			>
				<Input.Password placeholder="Password" />
			</Form.Item>

			<Form.Item
				name="staff_type"
				label="Staff Type"
				rules={[{ required: true, message: 'Please select a staff type' }]}
				initialValue="support"
			>
				<Select placeholder="Select staff type">
					<Select.Option value="support">Support</Select.Option>
					<Select.Option value="support_admin">Support Admin</Select.Option>
					<Select.Option value="insurance">Insurance</Select.Option>
				</Select>
			</Form.Item>

			<Form.Item className="mb-0">
				<Button
					type="primary"
					htmlType="submit"
					loading={isLoading}
					className="w-full"
				>
					Create Admin Account
				</Button>
			</Form.Item>
		</Form>
	);

	return (
		<AppLayout
			title="Create Admin"
			subtitle="Register a new admin account."
		>
			<div className="mx-auto max-w-[600px]">{modalContent}</div>

			<Modal
				open={showModal}
				onCancel={handleModalClose}
				width={600}
				footer={null}
				destroyOnClose
				title="Admin Registration"
				maskClosable={false}
				centered
			>
				{modalContent}
			</Modal>
		</AppLayout>
	);
};

export default CreateAdmin;
